import type { CriterionKey, PotteryStudio } from "@data/studios";
import { getStudioBookingCtaLabel } from "@/lib/studioCta";
import { RatingPill } from "./RatingPill";
import styles from "./StudioCard.module.css";

interface StudioCardProps {
  studio: PotteryStudio;
}

const RATING_LABELS: Array<{ key: CriterionKey; label: string }> = [
  { key: "instruction", label: "Instruction" },
  { key: "facilities", label: "Facilities" },
  { key: "community", label: "Community" },
  { key: "value", label: "Value" },
  { key: "beginner", label: "Beginners" },
];

export function StudioCard({ studio }: StudioCardProps) {
  const isTop = studio.rank === 1;
  const standouts = RATING_LABELS.filter(
    ({ key }) => studio.ratings[key] === "exceptional",
  ).map(({ label }) => label);

  return (
    <article
      className={`${styles.card} ${isTop ? styles.top : ""}`}
      aria-labelledby={`studio-${studio.id}-title`}
    >
      <div className={styles.header}>
        <span className={styles.rankValue}>
          <span className={styles.rankHash}>#</span>
          {studio.rank}
        </span>
        <div className={styles.titleBlock}>
          <div className={styles.titleRow}>
            <h3 id={`studio-${studio.id}-title`}>{studio.name}</h3>
          </div>
          <p className={styles.location}>
            {studio.neighborhood}, {studio.borough}
          </p>
        </div>
        {studio.accolade && <span className={styles.accolade}>{studio.accolade}</span>}
      </div>

      <p className={styles.description}>{studio.description}</p>

      {standouts.length > 0 && (
        <p className={styles.standout}>
          <span className={styles.standoutLabel}>Stands out for</span>
          {standouts.join(" · ")}
        </p>
      )}

      <div className={styles.body}>
        <div className={styles.left}>
          <div>
            <h4>Highlights</h4>
            <ul>
              {studio.highlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className={styles.tags}>
            {studio.classTypes.map((type) => (
              <span key={type} className={styles.tag}>
                {type}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.ratings}>
          <h4>How it rates</h4>
          {RATING_LABELS.map(({ key, label }) => (
            <RatingPill key={key} label={label} rating={studio.ratings[key]} />
          ))}
        </div>
      </div>

      <p className={styles.note}>{studio.editorialNote}</p>

      <div className={styles.actions}>
        <a
          href={studio.website}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.primaryCta}
        >
          {getStudioBookingCtaLabel(studio.name)}
        </a>
        <a
          href={studio.website}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.secondaryCta}
        >
          Visit website
        </a>
      </div>
    </article>
  );
}
